import { LoanLightButtonWrapper } from "@/components/legacy-wrappers/LoanLightButtonWrapper";
import { LoanLightTopBarWrapper } from "@/components/legacy-wrappers/LoanLightTopBarWrapper";
import { closeInteraction, submitTransaction } from "@/services/encompass.service";
import { AuditDetail, AuditState, TransactionTypeEnum } from "@/types/epc-interfaces";
import { getSvg } from "@/utils/getSvgs";
import { useEllimae } from "@/store/useEllimae";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuditProgressService } from "@/services/audit-progress.service";
import { LoanLightLoading } from "@/components/LoanLightLoading";
import { Button, Divider } from "@loanlight-engineering/shared-ui/primitives";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
const STARTING_PROGRESS_PERCENT = 0;
const STARTING_PROGRESS_MESSAGE = "Starting audit...";
export const AuditRunning = () => {
  const ellimaeState = useEllimae(state => state);
  const ellimaeStateRef = useRef(ellimaeState);
  const hasStartedRef = useRef(false);
  const progressPercentRef = useRef(STARTING_PROGRESS_PERCENT);
  const progressMessageRef = useRef(STARTING_PROGRESS_MESSAGE);
  const progressLogRef = useRef<string[]>([]);
  const [progressLog, setProgressLog] = useState<string[]>([]);
  const [progressMessage, setProgressMessage] = useState(STARTING_PROGRESS_MESSAGE);
  const [progressPercent, setProgressPercent] = useState(STARTING_PROGRESS_PERCENT);
  const [retrying, setRetrying] = useState(false);
  console.log("[AuditRunning] Rendering AuditRunning component");
  const auditState = ellimaeState.originResponse?.auditDetail?.auditState;
  const hasError = auditState === AuditState.Error;
  const auditProgressService = useMemo(() => AuditProgressService.create(), []);
  useEffect(() => {
    ellimaeStateRef.current = ellimaeState;
  }, [ellimaeState]);
  const updateProgressPercent = (value: number) => {
    progressPercentRef.current = value;
    setProgressPercent(value);
  };
  const updateProgressMessage = (message: string) => {
    progressMessageRef.current = message;
    setProgressMessage(message);
    if (!progressLogRef.current.length || progressLogRef.current[progressLogRef.current.length - 1] !== message) {
      progressLogRef.current = [...progressLogRef.current, message];
      setProgressLog([...progressLogRef.current]);
    }
  };
  const transactionId = ellimaeState.originResponse?.transactionId || ellimaeState.transactionId;
  const token = ellimaeState.token;
  const sessionId = ellimaeState.sessionId;
  const previousTransactionStateId = ellimaeState.originResponse?.auditDetail?.transactionStateId;
  useEffect(() => {
    if (!transactionId || !token) {
      console.error("[AuditRunning] Missing required data:", {
        hasTransactionId: !!transactionId,
        hasToken: !!token,
        hasOriginResponseTransactionId: !!ellimaeStateRef.current.originResponse?.transactionId,
        hasDirectTransactionId: !!ellimaeStateRef.current.transactionId
      });
      return;
    }
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    console.log("[AuditRunning] useEffect - starting streaming audit updates");
    void auditProgressService.startStreaming(transactionId, token, (state_0: AuditState, nextTransactionStateId: string, progressData?: {
      progressStep?: string | null;
      progressMessage?: string | null;
      progressPercent?: number | null;
    }) => {
      console.log("[AuditRunning] Callback received:", {
        state: state_0,
        transactionStateId: nextTransactionStateId,
        progressData,
        isComplete: state_0 === AuditState.Complete,
        isFailed: state_0 === AuditState.Failed
      });

      // Update progress display - enforce monotonic progress (never go backwards)
      if (typeof progressData?.progressPercent === "number") {
        if (progressData.progressPercent >= progressPercentRef.current) {
          // Progress increased or stayed same - update both message and percent
          updateProgressPercent(progressData.progressPercent);
          if (progressData?.progressMessage) {
            updateProgressMessage(progressData.progressMessage);
          }
        } else {
          // Progress went backwards - ignore this update (out-of-order event)
          console.log("[AuditRunning] Ignoring out-of-order progress:", {
            current: progressPercentRef.current,
            received: progressData.progressPercent,
            message: progressData.progressMessage
          });
        }
      } else if (progressData?.progressMessage) {
        // No percent provided, just update message
        updateProgressMessage(progressData.progressMessage);
      }
      if (state_0 === AuditState.Complete || state_0 === AuditState.Failed || state_0 === AuditState.Error) {
        console.log("[AuditRunning] Audit finished! Updating state and navigating...");

        // Set final progress
        if (state_0 === AuditState.Complete) {
          updateProgressMessage("✅ Audit complete!");
          updateProgressPercent(100);
        } else if (state_0 === AuditState.Error) {
          updateProgressMessage("⚠️ Error occurred - you can retry");
        }
        const currentState = ellimaeStateRef.current;
        const updatedAuditDetail = (state_0 === AuditState.Complete ? {
          ...currentState.originResponse?.auditDetail,
          auditState: state_0,
          transactionStateId: nextTransactionStateId,
          errorMessage: null,
          errorCode: null,
          errorStep: null,
          errorTimestamp: null
        } : {
          ...currentState.originResponse?.auditDetail,
          auditState: state_0,
          transactionStateId: nextTransactionStateId
        }) as AuditDetail;
        console.log("[AuditRunning] Dispatching UPDATE_APPLICATION_STATE:", updatedAuditDetail);
        currentState.setEllimaeState({
          originResponse: currentState.originResponse ? {
            ...currentState.originResponse,
            auditDetail: updatedAuditDetail
          } : null
        });
      } else {
        console.log("[AuditRunning] Audit still in progress:", {
          state: state_0,
          message: progressMessageRef.current,
          percent: progressPercentRef.current
        });
      }
    }, 2000,
    // initialDelayMs
    1800000,
    // timeoutMs (30 minutes)
    (error: Error) => {
      console.error("[AuditRunning] Audit error/timeout:", error);

      // Get transactionStateId if available from previous updates
      const currentState_0 = ellimaeStateRef.current;
      const currentTransactionStateId = currentState_0.originResponse?.auditDetail?.transactionStateId;

      // Update application state with error information so the audit page can display it
      // Use Error state (not Failed) since this is recoverable - user can retry
      const updatedAuditDetail_0 = {
        ...currentState_0.originResponse?.auditDetail,
        auditState: AuditState.Error,
        errorMessage: error.message,
        errorCode: "CONNECTION_LOST",
        errorStep: "streaming",
        errorTimestamp: new Date().toISOString()
      } as AuditDetail;
      currentState_0.setEllimaeState({
        originResponse: currentState_0.originResponse ? {
          ...currentState_0.originResponse,
          auditDetail: updatedAuditDetail_0 as AuditDetail
        } : null
      });
      if (!currentTransactionStateId) {
        updateProgressMessage("⚠️ Error occurred - you can retry");
      }
    },
    // onTimeout callback
    previousTransactionStateId,
    // Pass for stale data detection
    sessionId // Pass sessionId for backend correlation
    );
    return () => {
      auditProgressService.stopStreaming();
      hasStartedRef.current = false;
    };
  }, [auditProgressService, previousTransactionStateId, token, transactionId]);
  const handleClose = () => {
    (async () => {
      await closeInteraction();
    })().catch(e => {
      console.error(e);
    });
  };
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const transactionId_0 = await submitTransaction({
        requestType: TransactionTypeEnum.AUS,
        resources: ellimaeState.resources || [],
        transactionId: ellimaeState.transactionId ?? ellimaeState.originResponse?.auditDetail?.transactionStateId ?? null
      });
      if (transactionId_0) {
        ellimaeState.setEllimaeState({
          transactionId: transactionId_0
        });
      }
      progressLogRef.current = [];
      setProgressLog([]);
      hasStartedRef.current = false;
      updateProgressPercent(STARTING_PROGRESS_PERCENT);
      updateProgressMessage(STARTING_PROGRESS_MESSAGE);
      const updatedState = {
        ...ellimaeState.originResponse?.auditDetail,
        auditState: AuditState.Processing,
        errorMessage: null,
        errorCode: null,
        errorStep: null,
        errorTimestamp: null
      } as AuditDetail;
      ellimaeState.setEllimaeState({
        originResponse: ellimaeState.originResponse ? {
          ...ellimaeState.originResponse,
          auditDetail: updatedState
        } : null
      });
    } catch (e_0) {
      console.error("[AuditRunning] Retry failed:", e_0);
    } finally {
      setRetrying(false);
    }
  };
  return <div className="min-h-screen flex flex-col bg-brand-900">
      <LoanLightTopBarWrapper>
        <div slot="right">
          <LoanLightButtonWrapper variant="primary" size="small" onClick={handleClose}>
            <span dangerouslySetInnerHTML={{
            __html: getSvg("xSvg")
          }} />
          </LoanLightButtonWrapper>
        </div>
      </LoanLightTopBarWrapper>

      <div className="flex flex-1 flex-col items-center pt-[4vh]">
        <div className="w-148 rounded-2xl bg-white shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] flex flex-col items-center">
          <div className="flex flex-col items-center gap-4 p-8 w-full">
            {hasError ? <ExclamationTriangleIcon className="h-12 w-12 text-red-500" /> : <div className="flex items-center justify-center">
                <LoanLightLoading />
              </div>}
            <h2 className="m-0 text-center text-[32px] font-medium text-zinc-900 tracking-[-0.96px] leading-none">
              {hasError ? "Audit Error" : "Audit in progress"}
            </h2>
            <p className="m-0 text-center text-base font-normal text-brand-950 leading-normal tracking-[-0.32px]">
              {hasError ? "An error occurred while processing the audit. You can retry with the same documents." : "The results will automatically load when it\u2019s finished processing. LoanLight runs in the background so you can close this window and come back later."}
            </p>
            {hasError && progressLog.length > 0 && <>
                <Divider />
                <div className="w-full">
                  <p className="m-0 pb-2 text-sm font-medium text-zinc-700">
                    Progress log
                  </p>
                  <ul className="m-0 list-none p-0 space-y-1">
                    {progressLog.map((msg, i) => <li key={i} className="text-sm text-zinc-500 leading-normal tracking-[-0.28px]">
                        {msg}
                      </li>)}
                  </ul>
                </div>
              </>}
            <Divider />
            {hasError ? <div className="flex gap-3 w-full pt-4">
                <Button color="light" className="w-full" onClick={handleClose}>
                  Close
                </Button>
                <Button color="dark" className="w-full items-center" onClick={handleRetry} disabled={retrying}>
                  {retrying ? <span dangerouslySetInnerHTML={{
                __html: getSvg("spinningDotsSvg")
              }} /> : null}
                  Retry Audit
                </Button>
              </div> : <p className="m-0 pt-4 text-center text-base font-normal tracking-[-0.32px] text-zinc-500">
                Most audits finish within 5–15 minutes.
              </p>}
          </div>
        </div>
      </div>
    </div>;
};